import * as React from 'react';

import { Input as ShadcnInput } from '@/hen-ui/shadcn/input';
import { Icon, type IconName } from '@/hen-ui/atomics/Icon';
import { Flex } from '@/hen-ui/composites/Flex';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import {
  inputAffixVariants,
  inputPasswordToggleVariants,
  inputSpinnerVariants,
  inputVariants,
  type InputVariantProps,
} from './input.variants';
import { cn } from '@/hen-ui/utils';
import {
  fieldPlaceholderColorClass,
  fieldPlaceholderSizeClass,
  fieldTextColorClass,
  fieldTextSizeClass,
  type FieldTextStyleProps,
} from '@/hen-ui/utils/field-text-style';

export interface InputProps
  extends React.ComponentPropsWithoutRef<typeof ShadcnInput>,
    InputVariantProps,
    FieldTextStyleProps {
  /**
   * Error state - when true, displays error styling and sets aria-invalid
   */
  error?: boolean;
  /**
   * Error message text - when provided, sets aria-describedby and aria-invalid
   */
  errorMessage?: string;
  /**
   * Helper text below the input. Omitted when `errorMessage` is set (error takes precedence).
   */
  helperText?: string;
  /**
   * Icon name to display on the left side of the input (uses Icon atomic component).
   * Decorative only. Ignored on the start side when `startText` is set.
   */
  icon?: IconName;
  /**
   * Short text unit shown before the value and aligned with it.
   * Announced with the field. When set, replaces `icon`.
   */
  startText?: string;
  /**
   * Short text unit shown directly beside the typed value, such as % or mo.
   * Announced with the field. When set, replaces `endIcon`.
   */
  endText?: string;
  /**
   * Decorative icon on the end side of the value. Hidden from assistive tech.
   * Ignored when `endText` is set.
   */
  endIcon?: IconName;
  /**
   * Label text - when variant is 'two-line', this label will animate from placeholder to top position
   */
  label?: string;
  /**
   * Field-name weight for the floated `'two-line'` label. Defaults to `'bold'`.
   * `'regular'` renders a lighter label where a tenant's form calls for it.
   */
  fieldNameWeight?: 'bold' | 'regular';
  /**
   * Trailing control that reveals or conceals the value. Shown only when
   * `type` is `password`. Left off, the field renders as it does today.
   */
  showPasswordToggle?: boolean;
}

function visibleUnit(text: string | undefined): string | undefined {
  const trimmed = text?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Inset so the value starts where the unit ends. A measured width wins.
 * `ch` is only the first paint, because `%` and `mo` are wider than `0`.
 * A word unit gets a 4px gap so it does not run into the number. Symbols stay flush.
 */
function unitSize(text: string, measuredWidth: number): string {
  return measuredWidth > 0 ? `${measuredWidth}px` : `${text.length}ch`;
}

function unitGap(text: string): string {
  return /[a-z]/i.test(text) ? ' + var(--space-min, 4px)' : '';
}

function unitInset(
  text: string,
  measuredWidth: number,
  edge = 'var(--space-xs, 12px)',
  extra = '',
): string {
  return `calc(${edge} + ${unitSize(text, measuredWidth)}${unitGap(text)}${extra})`;
}

/** Room for the browser stepper so a long value stops before it. */
const NUMBER_STEPPER_EDGE = 'var(--space-xs, 12px) + var(--space-lg, 24px)';

/** Room for the password reveal control, matching its `right-2xl` slot. */
const PASSWORD_TOGGLE_EDGE =
  'var(--space-xs, 12px) + var(--space-lg, 24px) + var(--space-min, 4px)';

/**
 * First-paint place for an end unit: directly after the typed value.
 * Once the field has a width, layout measurement clamps this so a long
 * value stops before the trailing control.
 */
function endUnitBeside(
  valueText: string,
  measuredValue: number,
  startEdge: string,
  endText: string,
): string {
  const valueSize = valueText ? unitSize(valueText, measuredValue) : '0px';
  return `calc(${startEdge} + ${valueSize}${unitGap(endText)})`;
}

/** Keep the unit after the value and inside the field. */
function clampEndUnit(beside: number, parked: number): number {
  return Math.max(0, Math.min(beside, parked));
}

/** Everything a number field can hold; `,` is the decimal mark in some locales. */
const NUMBER_CHARACTERS = /^[0-9.,-]*$/;

/**
 * What a number field may show on the way to a real number without being
 * one yet: a lone "-", or digits ending in a decimal mark ("7.", "-.").
 */
const PENDING_NUMBER = /^-?(\d*\.)?$/;

/**
 * How many digits may follow the decimal point, from `step`: 0.1 allows one,
 * 0.01 two, and no step (the browser's default of 1) none. `any` allows any.
 */
function stepDecimals(step: number | string | undefined): number | null {
  if (step === 'any') {
    return null;
  }
  const size = numberLimit(step) ?? 1;
  if (size <= 0) {
    return null;
  }
  const [digits, exponent] = String(size).split('e-');
  const places = digits.split('.')[1]?.length ?? 0;
  return places + (exponent ? Number(exponent) : 0);
}

function numberLimit(limit: number | string | undefined): number | null {
  if (limit === undefined || limit === '') {
    return null;
  }
  const parsed = typeof limit === 'number' ? limit : Number(limit);
  return Number.isFinite(parsed) ? parsed : null;
}

function fieldValueText(next: unknown): string {
  if (typeof next === 'string' || typeof next === 'number') {
    return String(next);
  }
  if (Array.isArray(next)) {
    return next.map(String).join('');
  }
  return '';
}

/**
 * Input component following HEN Design System standards.
 *
 * A standardized text input field wrapping the native HTML input element
 * with HEN-specific variants and design tokens. All styling uses tokens
 * from tailwind.config.ts.
 *
 * @example
 * // Basic usage
 * <Input type="text" placeholder="Enter your name" />
 *
 * @example
 * // With label and id for accessibility
 * <label htmlFor="email-input">Email</label>
 * <Input id="email-input" type="email" placeholder="email@example.com" />
 *
 * @example
 * // With helper text (linked via aria-describedby; omitted when errorMessage is set)
 * <Input type="text" helperText="Use your legal name as it appears on ID." />
 *
 * @example
 * // With error state
 * <Input
 *   type="text"
 *   placeholder="Enter value"
 *   error
 *   errorMessage="This field is required"
 * />
 *
 * @example
 * // Disabled state
 * <Input type="text" placeholder="Disabled input" disabled />
 *
 * @example
 * // With icon (uses Icon atomic)
 * // <Input type="search" placeholder="Search..." icon="search" />
 *
 * @example
 * // Two-line label variant
 * <Input variant="two-line" label="Email Address" type="email" />
 */
export const Input = React.forwardRef<
  React.ElementRef<typeof ShadcnInput>,
  InputProps
>(
  (
    {
      className,
      variant,
      error,
      errorMessage,
      helperText,
      icon,
      startText,
      endText,
      endIcon,
      label,
      type,
      fieldNameWeight = 'bold',
      showPasswordToggle = false,
      placeholderSize,
      valueSize,
      placeholderColor,
      labelColor,
      id,
      value,
      defaultValue,
      disabled,
      style,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
      'aria-labelledby': ariaLabelledBy,
      onFocus,
      onBlur,
      onChange,
      step,
      min,
      max,
      placeholder,
      autoCapitalize,
      autoCorrect,
      spellCheck,
      ...props
    },
    ref,
  ) => {
    // Generate unique id if not provided (for aria-describedby)
    const inputId = React.useId();
    const finalId = id || inputId;
    const errorId = errorMessage ? `${finalId}-error` : undefined;
    const helperId =
      helperText && !errorMessage ? `${finalId}-helper` : undefined;
    const labelId = label ? `${finalId}-label` : undefined;
    const trimmedStart = visibleUnit(startText);
    const trimmedEnd = visibleUnit(endText);
    const showStartText = Boolean(trimmedStart);
    const showEndText = Boolean(trimmedEnd);
    const showStartIcon = !showStartText && Boolean(icon);
    const showPasswordToggleButton = showPasswordToggle && type === 'password';
    const showEndIcon =
      !showEndText && Boolean(endIcon) && !showPasswordToggleButton;
    const startUnitId = showStartText ? `${finalId}-start-unit` : undefined;
    const endUnitId = showEndText ? `${finalId}-end-unit` : undefined;
    const startUnitRef = React.useRef<HTMLSpanElement>(null);
    const endUnitRef = React.useRef<HTMLSpanElement>(null);
    const valueMeasureRef = React.useRef<HTMLSpanElement>(null);
    const gapProbeRef = React.useRef<HTMLSpanElement>(null);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    // The last number the field held that passed the checks below, restored
    // when an edit would leave it past `max` or unable to become a number.
    const committedNumberRef = React.useRef('');
    const insertedRef = React.useRef<string | null>(null);
    const [measuredStart, setMeasuredStart] = React.useState(0);
    const [measuredEnd, setMeasuredEnd] = React.useState(0);
    const [measuredValue, setMeasuredValue] = React.useState(0);
    const [placedLeft, setPlacedLeft] = React.useState<string | null>(null);
    const [uncontrolledValue, setUncontrolledValue] = React.useState(() =>
      fieldValueText(defaultValue),
    );
    const valueText =
      value !== undefined ? fieldValueText(value) : uncontrolledValue;
    // A number field can paint one invalid character while `value` stays empty.
    const [numberBadInput, setNumberBadInput] = React.useState(false);
    // Chrome paints "7." while `value` reads "7", and paints "-" while
    // `value` reads "". This holds what is actually painted, for measuring
    // the unit and for spotting a second decimal mark.
    const [paintedNumber, setPaintedNumber] = React.useState<string | null>(
      null,
    );
    const paintedNumberRef = React.useRef<string | null>(null);
    const paintNumber = React.useCallback((next: string | null) => {
      paintedNumberRef.current = next;
      setPaintedNumber(next);
    }, []);
    const placeholderText =
      typeof placeholder === 'string' ? placeholder : undefined;
    const measuringPlaceholder =
      !valueText &&
      !numberBadInput &&
      !paintedNumber &&
      Boolean(placeholderText);
    const mirrorText =
      paintedNumber ||
      valueText ||
      (numberBadInput ? '0' : '') ||
      placeholderText ||
      '';
    const [passwordRevealed, setPasswordRevealed] = React.useState(false);
    const showNativeSpinner =
      type === 'number' && step !== 'any' && !showEndIcon;
    const stepperAfterUnit = showNativeSpinner && showEndText;

    React.useLayoutEffect(() => {
      if (!trimmedStart && !trimmedEnd) {
        return;
      }

      const read = () => {
        const startWidth = startUnitRef.current?.offsetWidth ?? 0;
        const endWidth = endUnitRef.current?.offsetWidth ?? 0;
        const valueWidth =
          valueText || measuringPlaceholder || numberBadInput || paintedNumber
            ? (valueMeasureRef.current?.offsetWidth ?? 0)
            : 0;
        setMeasuredStart((current) =>
          current === startWidth ? current : startWidth,
        );
        setMeasuredEnd((current) =>
          current === endWidth ? current : endWidth,
        );
        setMeasuredValue((current) =>
          current === valueWidth ? current : valueWidth,
        );

        const input = inputRef.current;
        const unit = endUnitRef.current;
        if (
          !input ||
          !unit ||
          input.offsetWidth <= 0 ||
          unit.offsetWidth <= 0
        ) {
          setPlacedLeft((current) => (current === null ? current : null));
          return;
        }

        const computed = getComputedStyle(input);
        const borderLeft = parseFloat(computed.borderLeftWidth) || 0;
        const borderRight = parseFloat(computed.borderRightWidth) || 0;
        // Chrome reports an input's `clientWidth` without its padding, so a
        // narrow field would park the unit at 0, in front of the value.
        const innerWidth = input.offsetWidth - borderLeft - borderRight;
        const paddingLeft = parseFloat(computed.paddingLeft);
        const paddingRight = parseFloat(computed.paddingRight);
        if (!Number.isFinite(paddingLeft) || !Number.isFinite(paddingRight)) {
          setPlacedLeft((current) => (current === null ? current : null));
          return;
        }

        const gap =
          trimmedEnd && (/[a-z]/i.test(trimmedEnd) || measuringPlaceholder)
            ? (gapProbeRef.current?.offsetWidth ?? 0)
            : 0;
        const beside = borderLeft + paddingLeft + valueWidth + gap;
        // paddingRight already reserves the word gap, so add it back when
        // the unit is parked or the gap disappears in front of `mo`.
        const parked = borderLeft + innerWidth - paddingRight + gap;
        const nextLeft = `${clampEndUnit(beside, parked)}px`;
        setPlacedLeft((current) => (current === nextLeft ? current : nextLeft));
      };

      read();

      if (typeof ResizeObserver === 'undefined') {
        return;
      }

      const observer = new ResizeObserver(read);
      if (startUnitRef.current) {
        observer.observe(startUnitRef.current);
      }
      if (endUnitRef.current) {
        observer.observe(endUnitRef.current);
      }
      if (valueMeasureRef.current) {
        observer.observe(valueMeasureRef.current);
      }
      if (gapProbeRef.current) {
        observer.observe(gapProbeRef.current);
      }
      if (inputRef.current) {
        observer.observe(inputRef.current);
      }
      return () => observer.disconnect();
    }, [
      trimmedStart,
      trimmedEnd,
      valueText,
      measuredStart,
      measuredEnd,
      measuringPlaceholder,
      numberBadInput,
      paintedNumber,
      stepperAfterUnit,
      showPasswordToggleButton,
      showStartIcon,
      passwordRevealed,
    ]);

    // Track focus and value state for two-line label
    const [isFocused, setIsFocused] = React.useState(false);
    const [hasValue, setHasValue] = React.useState(
      Boolean(value || defaultValue),
    );
    const pendingCaret = React.useRef<{
      start: number | null;
      end: number | null;
    } | null>(null);
    const inputType =
      showPasswordToggleButton && passwordRevealed ? 'text' : type;

    const assignInputRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        inputRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    const rememberCaretIfInputFocused = () => {
      const field = inputRef.current;
      if (!field || document.activeElement !== field) {
        pendingCaret.current = null;
        return;
      }
      pendingCaret.current = {
        start: field.selectionStart,
        end: field.selectionEnd,
      };
    };

    React.useEffect(() => {
      if (!showPasswordToggleButton) {
        setPasswordRevealed(false);
      }
    }, [showPasswordToggleButton]);

    React.useLayoutEffect(() => {
      const pending = pendingCaret.current;
      if (!pending) {
        return;
      }
      pendingCaret.current = null;
      const field = inputRef.current;
      if (!field) {
        return;
      }
      field.focus();
      if (pending.start === null || pending.end === null) {
        return;
      }
      try {
        field.setSelectionRange(pending.start, pending.end);
      } catch (error) {
        if (!(error instanceof DOMException)) {
          throw error;
        }
      }
    }, [passwordRevealed]);

    // Keep hasValue in sync with the controlled `value` (or `defaultValue`)
    // so the floating label still lifts when the parent prefills or clears
    // the field programmatically (e.g., from URL params or async hydration).
    React.useEffect(() => {
      if (value !== undefined) {
        setHasValue(Boolean(value));
      } else if (defaultValue !== undefined) {
        setHasValue(Boolean(defaultValue));
      }
    }, [value, defaultValue]);

    // Number fields are guarded in two steps. Before the browser inserts
    // anything, only characters that are never valid anywhere are stopped:
    // letters, `e`, `+`, a minus where `min` rules out negatives, and a
    // second decimal mark. Everything else is left to the browser, and the
    // result is checked after it lands. The result cannot be predicted
    // instead: Chrome does not expose the caret on number fields, so an edit
    // in the middle, over a selection, or after a "." it has not committed
    // ("3." reads as "3") would be judged as the wrong number. Spinner,
    // arrow-key, and scroll steps arrive as plain inserts of the next amount
    // and pass both steps untouched. Native listeners: React's onBeforeInput
    // is unreliable on number fields, and this `input` listener runs before
    // React's, so a restored value never reaches `onChange`.
    React.useEffect(() => {
      const input = inputRef.current;
      if (!input || type !== 'number') {
        return;
      }
      const minNumber = numberLimit(min);
      const maxNumber = numberLimit(max);
      const decimals = stepDecimals(step);
      const noteCommitted = () => {
        if (!input.validity.badInput) {
          committedNumberRef.current = input.value;
        }
      };
      noteCommitted();

      const handleBeforeInput = (e: Event) => {
        const data = (e as InputEvent).data;
        insertedRef.current = data;
        if (!data) {
          return;
        }
        const painted = paintedNumberRef.current ?? input.value;
        const decimalMark = data === '.' || data === ',';
        if (
          !NUMBER_CHARACTERS.test(data) ||
          (data.includes('-') && minNumber !== null && minNumber >= 0) ||
          (decimalMark && (decimals === 0 || /[.,]/.test(painted))) ||
          (data === '-' && painted.includes('-'))
        ) {
          e.preventDefault();
          insertedRef.current = null;
        }
      };

      const restore = (previous: string) => {
        input.value = previous;
        setNumberBadInput(false);
        paintNumber(null);
      };

      const handleInput = (e: Event) => {
        const editType = (e as InputEvent).inputType ?? '';
        const inserted = insertedRef.current;
        insertedRef.current = null;
        const previous = committedNumberRef.current;
        const painted = paintedNumberRef.current;

        if (input.validity.badInput) {
          if (editType.startsWith('delete') || !inserted) {
            // Never fight a deletion; it is on the way back to a number.
            // Without a known insert there is nothing to judge either.
            setNumberBadInput(true);
            paintNumber(painted ? painted.slice(0, -1) || null : null);
            return;
          }
          const candidate = `${painted ?? previous}${inserted ?? ''}`.replace(
            ',',
            '.',
          );
          if (inserted && PENDING_NUMBER.test(candidate)) {
            setNumberBadInput(true);
            paintNumber(candidate);
            return;
          }
          // "36-", "1.2.3": no amount of further typing makes this a number.
          restore(previous);
          return;
        }

        const next = input.value;
        if (
          next !== '' &&
          maxNumber !== null &&
          Number(next) > maxNumber &&
          Number(next) >= 0
        ) {
          // More digits only take a non-negative number further past `max`.
          // Below `min` is left alone: typing 1 on the way to 15 passes it.
          restore(previous);
          return;
        }
        if (decimals !== null && (next.split('.')[1]?.length ?? 0) > decimals) {
          // Finer than `step` allows, such as 7.55 when only 7.5 can be valid.
          restore(previous);
          return;
        }

        setNumberBadInput(false);
        committedNumberRef.current = next;
        const tookDecimalMark = inserted === '.' || inserted === ',';
        const deletedLastDecimal =
          editType.startsWith('delete') &&
          previous.startsWith(`${next}.`) &&
          previous.length === next.length + 2;
        paintNumber(
          next !== '' &&
            !next.includes('.') &&
            (tookDecimalMark || deletedLastDecimal)
            ? `${next}.`
            : null,
        );
      };

      input.addEventListener('focus', noteCommitted);
      input.addEventListener('beforeinput', handleBeforeInput);
      input.addEventListener('input', handleInput);
      return () => {
        input.removeEventListener('focus', noteCommitted);
        input.removeEventListener('beforeinput', handleBeforeInput);
        input.removeEventListener('input', handleInput);
      };
    }, [type, min, max, step, paintNumber]);

    React.useEffect(() => {
      const input = inputRef.current;
      const form = input?.form;
      if (value !== undefined || !showEndText || !input || !form) {
        return;
      }

      const handleReset = () => {
        queueMicrotask(() => {
          setNumberBadInput(false);
          paintNumber(null);
          committedNumberRef.current = input.value;
          setUncontrolledValue(input.value);
          setHasValue(Boolean(input.value));
        });
      };

      form.addEventListener('reset', handleReset);
      return () => form.removeEventListener('reset', handleReset);
    }, [value, showEndText, paintNumber]);

    // Determine if label should be floating (at top)
    const isFloating = variant === 'two-line' && (isFocused || hasValue);

    // A two-line label starts out sitting in the placeholder's own spot, so
    // painting a native placeholder there too stacks both texts on top of
    // each other. Hold it back until the label floats to the top, where the
    // field has room to show its own hint.
    const nativePlaceholder =
      variant === 'two-line' && label && !isFloating ? undefined : placeholder;

    // Combine aria-describedby values (error message replaces helper in the DOM and ids)
    const describedBy =
      [ariaDescribedBy, startUnitId, endUnitId, helperId, errorId]
        .filter(Boolean)
        .join(' ') || undefined;

    // Merge the internal two-line label id with any caller-provided aria-labelledby
    // so external labelling is preserved instead of overwritten.
    const internalLabelledBy =
      variant === 'two-line' && label ? labelId : undefined;
    const labelledBy =
      [internalLabelledBy, ariaLabelledBy].filter(Boolean).join(' ') ||
      undefined;

    // Set aria-invalid based on error prop or errorMessage
    const isInvalid = error || Boolean(errorMessage);
    const finalAriaInvalid =
      ariaInvalid !== undefined ? ariaInvalid : isInvalid ? 'true' : undefined;

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      // Leaving the field drops a trailing "." or "-" the browser never
      // committed.
      paintNumber(null);
      onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const badInput = type === 'number' && e.target.validity.badInput;
      setNumberBadInput(badInput);
      if (value === undefined) {
        setUncontrolledValue(e.target.value);
      }
      setHasValue(Boolean(e.target.value) || badInput);
      onChange?.(e);
    };

    const affixPadding: React.CSSProperties = {};
    if (trimmedStart) {
      affixPadding.paddingLeft = unitInset(trimmedStart, measuredStart);
    }
    const endRightReserve = stepperAfterUnit
      ? NUMBER_STEPPER_EDGE
      : showPasswordToggleButton
        ? PASSWORD_TOGGLE_EDGE
        : 'var(--space-xs, 12px)';
    if (trimmedEnd) {
      affixPadding.paddingRight = unitInset(
        trimmedEnd,
        measuredEnd,
        endRightReserve,
      );
    }
    const valueSizeClass = valueSize
      ? fieldTextSizeClass[valueSize]
      : 'text-b5';
    const mirrorSizeClass = measuringPlaceholder
      ? fieldTextSizeClass[placeholderSize ?? 'b5']
      : valueSizeClass;

    const valueStartEdge = trimmedStart
      ? `var(--space-xs, 12px) + ${unitSize(trimmedStart, measuredStart)}${unitGap(trimmedStart)}`
      : showStartIcon
        ? 'var(--space-d2, 36px)'
        : 'var(--space-xs, 12px)';

    return (
      <Flex direction='column' gap='xxs' width='full'>
        <div className='relative isolate w-full'>
          {showEndText && (
            <span
              ref={valueMeasureRef}
              aria-hidden='true'
              className={cn(
                'pointer-events-none invisible absolute left-0 top-0 max-w-full overflow-hidden whitespace-pre font-body',
                mirrorSizeClass,
              )}
              style={
                showPasswordToggleButton && !passwordRevealed
                  ? ({ WebkitTextSecurity: 'disc' } as React.CSSProperties)
                  : undefined
              }
            >
              {mirrorText}
            </span>
          )}
          {showEndText &&
            trimmedEnd &&
            (/[a-z]/i.test(trimmedEnd) || measuringPlaceholder) && (
              <span
                ref={gapProbeRef}
                aria-hidden='true'
                className='pointer-events-none invisible absolute h-px'
                style={{ width: 'var(--space-min, 4px)' }}
              />
            )}
          {showStartText && trimmedStart && (
            <div
              className={inputAffixVariants({
                variant,
                side: 'start',
              })}
            >
              <span
                ref={startUnitRef}
                id={startUnitId}
                aria-hidden='true'
                className={cn(
                  'font-body text-subtext whitespace-nowrap',
                  valueSizeClass,
                )}
              >
                {trimmedStart}
              </span>
            </div>
          )}
          {showStartIcon && icon && (
            <div
              className={inputAffixVariants({
                variant,
                side: 'start',
              })}
              aria-hidden='true'
            >
              <Icon icon={icon} size='sm' className='shrink-0 text-subtext' />
            </div>
          )}
          {variant === 'two-line' && label && (
            <label
              id={labelId}
              htmlFor={finalId}
              className={cn(
                'absolute pointer-events-none z-10',
                showStartText && !isFloating
                  ? undefined
                  : showStartIcon && !isFloating
                    ? 'left-d2'
                    : 'left-xs',
                'transition-all duration-normal ease motion-reduce:transition-none',
                'text-body font-body',
                isFloating
                  ? cn(
                      'top-xxs text-l2',
                      fieldNameWeight === 'regular'
                        ? 'font-normal'
                        : 'font-bold',
                    )
                  : cn(
                      'top-1/2 -translate-y-1/2',
                      placeholderSize
                        ? fieldTextSizeClass[placeholderSize]
                        : 'text-b5 leading-[22px]',
                    ),
                isInvalid && isFloating
                  ? 'text-error-500'
                  : isFloating
                    ? labelColor
                      ? fieldTextColorClass[labelColor]
                      : 'text-body'
                    : placeholderColor
                      ? fieldTextColorClass[placeholderColor]
                      : 'text-subtext',
                showPasswordToggleButton && 'right-2xl',
              )}
              style={
                trimmedStart && !isFloating
                  ? { left: unitInset(trimmedStart, measuredStart) }
                  : undefined
              }
            >
              {label}
            </label>
          )}
          <ShadcnInput
            {...props}
            className={cn(
              inputVariants({ variant, error: isInvalid }),
              valueSize && fieldTextSizeClass[valueSize],
              // ::placeholder inherits the field's size, so pin it once valueSize moves that.
              (placeholderSize || valueSize) &&
                fieldPlaceholderSizeClass[placeholderSize ?? 'b5'],
              placeholderColor && fieldPlaceholderColorClass[placeholderColor],
              showStartIcon && 'pl-d2',
              showEndIcon && 'pr-d2',
              showPasswordToggleButton && !showEndText && 'pr-2xl',
              type === 'number' &&
                inputSpinnerVariants({
                  show: showNativeSpinner,
                  afterUnit: stepperAfterUnit,
                  variant,
                }),
              className,
            )}
            type={inputType}
            disabled={disabled}
            autoCapitalize={
              autoCapitalize ?? (showPasswordToggleButton ? 'none' : undefined)
            }
            autoCorrect={
              autoCorrect ?? (showPasswordToggleButton ? 'off' : undefined)
            }
            spellCheck={
              spellCheck ?? (showPasswordToggleButton ? false : undefined)
            }
            style={
              showStartText || showEndText
                ? { ...style, ...affixPadding }
                : style
            }
            ref={assignInputRef}
            step={step}
            min={min}
            max={max}
            placeholder={nativePlaceholder}
            id={finalId}
            value={value}
            defaultValue={defaultValue}
            aria-invalid={finalAriaInvalid}
            aria-describedby={describedBy}
            aria-labelledby={labelledBy}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            data-hen-id={COMPONENT_IDS.Input}
          />
          {showEndText && trimmedEnd && (
            <div
              className={inputAffixVariants({
                variant,
                side: 'beside',
              })}
              style={{
                left:
                  placedLeft ??
                  endUnitBeside(
                    valueText,
                    measuredValue,
                    valueStartEdge,
                    trimmedEnd,
                  ),
              }}
            >
              <span
                ref={endUnitRef}
                id={endUnitId}
                aria-hidden='true'
                className={cn(
                  'font-body text-subtext whitespace-nowrap',
                  valueSizeClass,
                )}
              >
                {trimmedEnd}
              </span>
            </div>
          )}
          {showEndIcon && endIcon && (
            <div
              className={inputAffixVariants({
                variant,
                side: 'end',
              })}
              aria-hidden='true'
            >
              <Icon
                icon={endIcon}
                size='sm'
                className='shrink-0 text-subtext'
              />
            </div>
          )}
          {showPasswordToggleButton && (
            <button
              type='button'
              className={cn(
                inputAffixVariants({ variant: 'default', side: 'end' }),
                inputPasswordToggleVariants(),
              )}
              aria-label={passwordRevealed ? 'Hide password' : 'Show password'}
              aria-controls={finalId}
              disabled={disabled}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                rememberCaretIfInputFocused();
                setPasswordRevealed((current) => !current);
              }}
            >
              <Icon
                icon={passwordRevealed ? 'eye-off' : 'eye'}
                size='sm'
                aria-hidden
              />
            </button>
          )}
        </div>
        {helperId && (
          <div id={helperId} className='text-sm text-subtext'>
            {helperText}
          </div>
        )}
        {errorMessage && (
          <div
            id={errorId}
            className='text-sm text-error-500'
            role='alert'
            aria-live='polite'
          >
            {errorMessage}
          </div>
        )}
      </Flex>
    );
  },
);

Input.displayName = 'Input';
